import { JSX, useState } from "react";
import { useTranslation } from "react-i18next";

import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";

import { asTitle } from "utils/Utils";
import { Event } from "utils/history/Event";

import {
	Report,
	Role
} from "core/blueprint/trajectory/TrajectoryBlueprintService";
import ReportRoleRow from "core/trajectory/access/ReportRoleRow";
import ReportSubHeader from "core/trajectory/access/ReportSubHeader";
import RoleInsertRow from "core/trajectory/access/RoleInsertRow";
import ReportFieldRoleRow from "./ReportFieldRoleRow";

export interface ReportRoleTableProps {
	roles: Role[];
	reports: Report[];
	editable: boolean;

	isValid: (name: string) => boolean;
	onInsert: (role: Role) => void;
	onChange: (event: Event) => void;
	onDelete: (index: number) => void;
}

function ReportRoleTable(props: ReportRoleTableProps): JSX.Element {
	const { t } = useTranslation();

	const [selection, setSelection] = useState<Array<string>>([]);
	const [selectedFields, setSelectedFields] = useState<Array<string>>([]);

	return (
		<>
			<ReportSubHeader
				reports={props.reports}
				selection={selection}
				selectedFields={selectedFields}
				onSelect={setSelection}
				onSelectFields={setSelectedFields}
			/>
			<Table size="small">
				<TableHead>
					<TableRow>
						<TableCell>{asTitle(t("role"))}</TableCell>
						{selectedFields.length === 0 && (
							<TableCell
								padding="checkbox"
								sx={{ minWidth: 100 }}
								align="center"
								id="admin-header">
								{asTitle(t("admin"))}
							</TableCell>
						)}
						<TableCell
							padding="checkbox"
							sx={{ minWidth: 100 }}
							align="center"
							id="edit-header">
							{asTitle(t("edit"))}
						</TableCell>
						<TableCell
							padding="checkbox"
							sx={{ minWidth: 100 }}
							align="center"
							id="read-header">
							{asTitle(t("read"))}
						</TableCell>
						<TableCell
							padding="checkbox"
							sx={{ minWidth: 100 }}
							align="center"
							id="hidden-header">
							{asTitle(t("hidden"))}
						</TableCell>
						<TableCell sx={{ width: 60 }} padding="none" />
					</TableRow>
				</TableHead>
				<TableBody>
					{selectedFields.length === 0
						? props.roles.map((role, index) => (
								<ReportRoleRow
									key={role.name}
									index={index}
									role={role}
									selected={selection}
									editable={props.editable}
									onChange={props.onChange}
									onDelete={props.onDelete}
								/>
						  ))
						: props.roles.map((role, index) => (
								<ReportFieldRoleRow
									key={role.name}
									index={index}
									role={role}
									report={selection[0]}
									selected={selectedFields}
									editable={props.editable}
									onChange={props.onChange}
									onDelete={props.onDelete}
								/>
						  ))}
					{props.editable && (
						<RoleInsertRow
							colSpan={selectedFields.length === 0 ? 4 : 3}
							isValid={props.isValid}
							onInsert={props.onInsert}
						/>
					)}
				</TableBody>
			</Table>
		</>
	);
}

export default ReportRoleTable;
